import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import QRCodeStyling from 'qr-code-styling';
import JSZip from 'jszip';
import Layout from '../components/Layout/Layout';
import StyledQRCode from '../components/StyledQRCode/StyledQRCode';
import { getQRConfig } from '../components/StyledQRCode/StyledQRCode';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const QRCodeGenerator = () => {
  const [attractions, setAttractions] = useState([]);
  const [selectedAttraction, setSelectedAttraction] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [popupStep, setPopupStep] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    const fetchAttractions = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axios.get(`${API_BASE_URL}/api/attractions?limit=1000&offset=0&language=english`);
        if (response.data && response.data.data) {
          setAttractions(response.data.data);
        } else {
          setError('No attractions data received from API');
        }
      } catch (err) {
        console.error('Error fetching attractions:', err);
        setError(`Failed to fetch attractions: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchAttractions();
  }, []);

  const generateQRCode = (attractionId, attractionName) => {
    if (!attractionId) {
      setError('Please select an attraction.');
      return;
    }
    setError('');
    setSelectedAttraction(attractionId);
    setPopupStep(1);
    setShowQRModal(true);
  };

  const proceedToQRGeneration = () => {
    setPopupStep(2);
  };

  const handleDownload = async () => {
    if (!selectedAttraction) return;
    const attraction = attractions.find((attr) => attr.id === selectedAttraction);
    const safeName = attraction
      ? attraction.name.replace(/[^a-zA-Z0-9]/g, '_')
      : selectedAttraction;
    try {
      await qrRef.current?.download(`${safeName}_QR_Code`);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download QR code. Please try again.');
    }
  };

  const closeModal = () => {
    setShowQRModal(false);
    setPopupStep(1);
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = filteredAttractions
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .map(a => a.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) return;
    setBulkDownloading(true);
    setError('');
    try {
      const zip = new JSZip();
      const selected = attractions.filter(a => selectedIds.has(a.id));

      for (const attraction of selected) {
        const url = `https://visitmelaka.ai/qr/${attraction.id}`;
        const qr = new QRCodeStyling(getQRConfig(url, 1024));
        const blob = await qr.getRawData('png');
        const safeName = attraction.name.replace(/[^a-zA-Z0-9]/g, '_');
        zip.file(`${safeName}_QR_Code.png`, blob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `visitmelaka_qr_codes_${today}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Bulk download failed:', err);
      setError('Bulk download failed. Please try again.');
    } finally {
      setBulkDownloading(false);
    }
  };

  // Filter attractions
  const filteredAttractions = attractions.filter(attraction => {
    const matchesSearch = !searchTerm ||
      attraction.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attraction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attraction.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attraction.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || attraction.category === selectedCategory;
    const matchesDistrict = !selectedDistrict || attraction.district === selectedDistrict;
    return matchesSearch && matchesCategory && matchesDistrict;
  });

  const categories = [...new Set(attractions.map(a => a.category))].filter(Boolean);
  const districts = [...new Set(attractions.map(a => a.district))].filter(Boolean);

  const pageAttractions = filteredAttractions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const allPageSelected = pageAttractions.length > 0 && pageAttractions.every(a => selectedIds.has(a.id));

  return (
    <Layout>
      <div className="container-fluid d-flex justify-content-center" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '20px' }}>
        <div style={{ maxWidth: '1200px', width: '100%' }}>
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1" style={{ color: '#2c3e50', fontWeight: 'bold', fontSize: '28px' }}>
              QR Code Generator
            </h2>
            <p className="text-muted mb-0" style={{ fontSize: '16px' }}>
              Generate QR codes for your attractions
            </p>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="card" style={{
          borderRadius: '15px',
          border: 'none',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          margin: '0 auto'
        }}>
          <div className="card-body" style={{ padding: '30px' }}>
            {/* Title Section */}
            <div className="d-flex align-items-center mb-4">
              <div className="d-flex align-items-center">
                <h3 className="mb-0 me-3" style={{ color: '#2c3e50', fontWeight: 'bold' }}>
                  Manage QR Codes
                </h3>
                <div className="d-flex align-items-center" style={{ color: '#e74c3c' }}>
                  <i className="fas fa-mobile-alt me-2" style={{ fontSize: '20px' }}></i>
                </div>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="mb-4">
              <div className="search-container mb-4" style={{ position: 'relative' }}>
                <div className="search-input-wrapper" style={{ position: 'relative' }}>
                  <i className="fas fa-search search-icon" style={{
                    position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)',
                    color: '#6c757d', fontSize: '16px', zIndex: 2
                  }}></i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search attractions by name, description, address, or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      border: '2px solid #dee2e6', borderRadius: '8px', fontSize: '16px',
                      padding: '12px 16px 12px 45px', height: '50px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)', backgroundColor: '#ffffff'
                    }}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap gap-4 align-items-center">
                <select className="form-select" value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{ minWidth: '180px', height: '45px', border: '2px solid #dee2e6', borderRadius: '0.375rem', fontSize: '16px', padding: '8px 12px' }}>
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>

                <select className="form-select" value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  style={{ minWidth: '180px', height: '45px', border: '2px solid #dee2e6', borderRadius: '0.375rem', fontSize: '16px', padding: '8px 12px' }}>
                  <option value="">All Districts</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>

                <select className="form-select" value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  style={{ minWidth: '150px', height: '48px', border: '1px solid #ced4da', borderRadius: '8px', fontSize: '16px', padding: '8px 12px', boxShadow: 'none' }}>
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>

                <button className="btn btn-dark" onClick={() => { setSearchTerm(''); setSelectedCategory(''); setSelectedDistrict(''); }}
                  style={{ borderRadius: '0.375rem', minWidth: '150px', height: '45px', fontSize: '16px', fontWeight: '500', padding: '8px 16px' }}>
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Bulk Selection Toolbar */}
            {selectedIds.size > 0 && (
              <div className="d-flex align-items-center gap-3 mb-3 p-3" style={{ backgroundColor: '#e3f2fd', borderRadius: '10px', border: '1px solid #90caf9' }}>
                <span style={{ fontWeight: '600', color: '#1565c0' }}>{selectedIds.size} selected</span>
                <button className="btn btn-primary btn-sm" onClick={handleBulkDownload} disabled={bulkDownloading}
                  style={{ borderRadius: '6px', padding: '6px 16px' }}>
                  <i className="fas fa-download me-1"></i>
                  {bulkDownloading ? 'Generating ZIP...' : 'Download Selected as ZIP'}
                </button>
                <button className="btn btn-link btn-sm text-muted" onClick={() => setSelectedIds(new Set())}
                  style={{ textDecoration: 'none' }}>
                  Clear selection
                </button>
              </div>
            )}

            {/* Results Count */}
            <div className="d-flex align-items-center mb-3" style={{ backgroundColor: '#e8f4fd', padding: '15px', borderRadius: '10px', border: '1px solid #bee5eb' }}>
              <i className="fas fa-chart-bar me-2" style={{ color: '#3498db', fontSize: '18px' }}></i>
              <span className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                Showing 1-{Math.min(itemsPerPage, filteredAttractions.length)} of {filteredAttractions.length} attractions
              </span>
            </div>

            {/* Error Display */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ borderRadius: '10px' }}>
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {/* Loading Spinner */}
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted" style={{ fontSize: '16px' }}>Loading attractions...</p>
              </div>
            )}

            {/* Attractions Table */}
            {!loading && filteredAttractions.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th className="border-0 py-3 px-3" style={{ width: '40px' }}>
                        <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      </th>
                      <th className="border-0 py-3 px-4 fw-bold text-dark">
                        NAME <i className="fas fa-sort-up ms-1"></i>
                      </th>
                      <th className="border-0 py-3 px-4 fw-bold text-dark">
                        CATEGORY <i className="fas fa-sort ms-1"></i>
                      </th>
                      <th className="border-0 py-3 px-4 fw-bold text-dark">
                        DISTRICT <i className="fas fa-sort ms-1"></i>
                      </th>
                      <th className="border-0 py-3 px-4 fw-bold text-dark">
                        DATE AND TIME OF ENTRY <i className="fas fa-sort ms-1"></i>
                      </th>
                      <th className="border-0 py-3 px-4 fw-bold text-dark">
                        QR CODE
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageAttractions.map((attraction, index) => (
                      <tr key={attraction.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                        <td className="py-3 px-3">
                          <input type="checkbox" checked={selectedIds.has(attraction.id)}
                            onChange={() => toggleSelect(attraction.id)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                        </td>
                        <td className="py-3 px-4 fw-bold text-dark">
                          {attraction.name}
                        </td>
                        <td className="py-3 px-4">
                          <span className="badge bg-secondary" style={{ borderRadius: '6px' }}>
                            {attraction.category}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-muted">
                          {attraction.district || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          {attraction.created_at ? new Date(attraction.created_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); generateQRCode(attraction.id, attraction.name); }}
                            title="Generate QR Code"
                            style={{ borderRadius: '6px', padding: '6px 12px' }}>
                            <i className="fas fa-qrcode me-1"></i>
                            Generate
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredAttractions.length > 0 && (
              <div className="d-flex justify-content-center align-items-center mt-4">
                <div className="pagination-container" style={{
                  backgroundColor: 'white', borderRadius: '12px', padding: '12px 20px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                  <button className="btn" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}
                    style={{ borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '500',
                      backgroundColor: currentPage === 1 ? '#f8f9fa' : '#6c757d',
                      color: currentPage === 1 ? '#adb5bd' : 'white', border: 'none',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                    Previous
                  </button>
                  <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', color: '#495057', margin: '0 8px' }}>
                    Page {currentPage} of {Math.ceil(filteredAttractions.length / itemsPerPage)}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {Array.from({ length: Math.ceil(filteredAttractions.length / itemsPerPage) }, (_, i) => i + 1)
                      .filter(page => {
                        const totalPages = Math.ceil(filteredAttractions.length / itemsPerPage);
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2;
                      })
                      .map((page, index, array) => {
                        const showEllipsis = index > 0 && page - array[index - 1] > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && <span style={{ padding: '8px 4px', color: '#6c757d', fontSize: '14px' }}>...</span>}
                            <button className="btn" onClick={() => setCurrentPage(page)}
                              style={{ borderRadius: '8px', padding: '8px 12px', fontSize: '14px', fontWeight: '500',
                                backgroundColor: currentPage === page ? '#007bff' : '#e9ecef',
                                color: currentPage === page ? 'white' : '#495057', border: 'none', cursor: 'pointer', minWidth: '40px' }}>
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>
                  <button className="btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredAttractions.length / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(filteredAttractions.length / itemsPerPage)}
                    style={{ borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '500',
                      backgroundColor: currentPage === Math.ceil(filteredAttractions.length / itemsPerPage) ? '#f8f9fa' : '#007bff',
                      color: currentPage === Math.ceil(filteredAttractions.length / itemsPerPage) ? '#adb5bd' : 'white',
                      border: 'none', cursor: currentPage === Math.ceil(filteredAttractions.length / itemsPerPage) ? 'not-allowed' : 'pointer' }}>
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* No Results Message */}
            {!loading && filteredAttractions.length === 0 && attractions.length > 0 && (
              <div className="text-center py-5">
                <i className="fas fa-search" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                <h5 className="mt-3 text-muted">No attractions found</h5>
                <p className="text-muted">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </div>

        {/* QR Code Modal */}
        {showQRModal && ReactDOM.createPortal(
          <div style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1051, width: '90%', maxWidth: '500px' }}>
              <div style={{ borderRadius: '15px', border: 'none', backgroundColor: 'white', boxShadow: 'none', width: '100%', overflow: 'hidden' }}>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '15px 15px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem' }}>
                  <h5 style={{ margin: 0 }}>
                    <i className="fas fa-qrcode me-2" style={{ color: '#3498db' }}></i>
                    {popupStep === 1 ? 'Preview Tracking Link' : 'Generated QR Code'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div style={{ padding: '30px', textAlign: 'center' }}>
                  {popupStep === 1 ? (
                    <div>
                      <div className="mb-4">
                        <i className="fas fa-link text-primary mb-3" style={{ fontSize: '48px' }}></i>
                        <h4 className="mb-3" style={{ color: '#2c3e50' }}>Preview Tracking Link</h4>
                        <p className="text-muted mb-4">This tracking URL will be encoded in the QR code. When scanned, it logs the visit then redirects to the attraction page.</p>
                      </div>
                      <div className="mb-4 p-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', border: '2px solid #dee2e6' }}>
                        <p className="text-muted mb-3">
                          <strong style={{ color: '#2c3e50' }}>Tracking URL:</strong>
                        </p>
                        <code style={{ fontSize: '16px', backgroundColor: '#ffffff', padding: '12px 16px', borderRadius: '8px', border: '1px solid #dee2e6', color: '#495057', wordBreak: 'break-all', display: 'block', textAlign: 'left' }}>
                          {`https://visitmelaka.ai/qr/${selectedAttraction}`}
                        </code>
                      </div>
                      <div className="d-flex gap-3 justify-content-center">
                        <button className="btn btn-primary" onClick={proceedToQRGeneration}
                          style={{ borderRadius: '8px', padding: '12px 24px', fontSize: '16px' }}>
                          <i className="fas fa-arrow-right me-2"></i>Generate QR Code
                        </button>
                        <button className="btn btn-secondary" onClick={closeModal}
                          style={{ borderRadius: '8px', padding: '12px 24px', fontSize: '16px' }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-center mb-4" style={{ width: '320px', margin: '0 auto' }}>
                        <StyledQRCode ref={qrRef} value={`https://visitmelaka.ai/qr/${selectedAttraction}`} size={1024} />
                      </div>
                      <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #dee2e6' }}>
                        <p className="text-muted mb-2"><strong style={{ color: '#2c3e50' }}>Tracking URL:</strong></p>
                        <code style={{ fontSize: '14px', backgroundColor: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6', color: '#495057', wordBreak: 'break-all', display: 'block', textAlign: 'left' }}>
                          {`https://visitmelaka.ai/qr/${selectedAttraction}`}
                        </code>
                      </div>
                      <div className="d-flex gap-2 justify-content-center">
                        <button className="btn btn-primary" onClick={handleDownload} style={{ borderRadius: '8px', padding: '10px 20px' }}>
                          <i className="fas fa-download me-1"></i>Download QR Code
                        </button>
                        <button className="btn btn-secondary" onClick={() => setPopupStep(1)} style={{ borderRadius: '8px', padding: '10px 20px' }}>
                          <i className="fas fa-arrow-left me-1"></i>Back to Preview
                        </button>
                        <button className="btn btn-outline-secondary" onClick={closeModal} style={{ borderRadius: '8px', padding: '10px 20px' }}>Close</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
        </div>
      </div>
    </Layout>
  );
};

export default QRCodeGenerator;
